import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';

import { MinusPhraseLibraryNameCell } from '../../components/MinusPhraseLibraryNameCell/MinusPhraseLibraryNameCell';
import { MinusPhraseLibraryPhrasesCell } from '../../components/MinusPhraseLibraryPhrasesCell/MinusPhraseLibraryPhrasesCell';
import { MinusPhraseLibraryGroupsCountCellContainer } from '../../containers/MinusPhraseLibraryGroupsCountCellContainer/MinusPhraseLibraryGroupsCountCellContainer';
import { MinusPhraseLibraryActionsMenuContainer } from '../../containers/MinusPhraseLibraryMenuActionsContainer/MinusPhraseLibraryMenuActionsContainer';
import { ColumnsSettings, ColumnsSettingsViewDict, minusPhraseLibrary } from '../../typings/common';
import { GridCellProps, GridColumnDecl, SortDirection } from '../../typings/grid';
import { MinusPhraseLibraryActions, MinusPhraseLibraryRow } from '../../typings/minusPhraseLibrary';
import { Omit } from '../../typings/utils';

import * as keyset from './minus-phrase-library.i18n';

const i18n = i18nFactory(keyset);

export function getStaticMinusPhraseLibraryColumns(): Array<Omit<GridColumnDecl<MinusPhraseLibraryRow>, 'width'>> {
    return [
        {
            title: '',
            key: 'settings',
            cellClass: 'settings-cell',
            mandatory: true,
            isSortable: false,
            minWidth: 40,
            maxWidth: 40,
            CellComponent: React.memo(
                ({
                    rowData: minusPhrase,
                    rowIndex,
                    actions
                }: GridCellProps<MinusPhraseLibraryRow, MinusPhraseLibraryActions>): JSX.Element => (
                    <MinusPhraseLibraryActionsMenuContainer index={rowIndex} actions={actions} />
                )
            )
        },
        {
            title: i18n('name'),
            key: 'name',
            cellClass: 'minus-phrase-name-cell',
            isSortable: false,
            defaultSortDirection: SortDirection.asc,
            minWidth: 105,
            CellComponent: React.memo(
                ({ rowData: { name, id }, rowIndex, actions }: GridCellProps<MinusPhraseLibraryRow>): JSX.Element => (
                    <MinusPhraseLibraryNameCell name={name} id={id} />
                )
            )
        },
        {
            title: i18n('preview'),
            key: 'preview',
            cellClass: 'minus-phrase-preview-cell',
            isSortable: false,
            defaultSortDirection: SortDirection.asc,
            minWidth: 105,
            CellComponent: React.memo(
                ({
                    rowData: { minusKeywords },
                    rowIndex,
                    actions
                }: GridCellProps<MinusPhraseLibraryRow>): JSX.Element => (
                    <MinusPhraseLibraryPhrasesCell minusPhrases={minusKeywords} />
                )
            )
        },
        {
            title: i18n('groups'),
            key: 'groups',
            cellClass: 'minus-phrase-groups-cell',
            isSortable: false,
            align: 'right',
            defaultSortDirection: SortDirection.asc,
            minWidth: 80,
            CellComponent: React.memo(
                ({ rowData: { groupsCount, id } }: GridCellProps<MinusPhraseLibraryRow>): JSX.Element => (
                    <MinusPhraseLibraryGroupsCountCellContainer groupsCount={groupsCount} id={id} />
                )
            )
        }
    ];
}

export function getDefaultDynamicMinusPhraseLibraryColumns(): ColumnsSettings {
    return {
        columnOrder: ['settings', 'name', 'preview', 'groups'],
        columnViewInfo: {
            settings: {
                width: 40
            },
            name: {
                width: 240
            },
            preview: {
                width: 800
            },
            groups: {
                width: 80
            }
        }
    };
}

export function getMinusPhraseLibraryColumns(
    columnsSettings: ColumnsSettingsViewDict
): Array<GridColumnDecl<MinusPhraseLibraryRow>> {
    return getStaticMinusPhraseLibraryColumns().map(column => {
        return {
            ...column,
            ...columnsSettings[minusPhraseLibrary].columnViewInfo[column.key]
        };
    });
}
